import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TaskBoardSectionSchema, type TaskBoardSection, type TaskBoardTask } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Disclosure } from "@/components/ui/disclosure";
import type { AuthSession } from "../../auth/keycloak.js";
import { formatDate } from "../pm-board/pm-labels.js";
import { PmMarkdown } from "../pm-board/PmMarkdown.js";
import { StatusChip } from "../pm-board/StatusChip.js";
import { TaskGoneError, useTaskBoardClosed, useTaskBoardFocusRefresh, useTaskBoardTask, useTaskBoardTasks } from "./useTaskBoard.js";

/** Whether this document is the task board — read at boot from the pathname, like `/pm`. */
export function isTaskBoardPath(pathname: string): boolean {
  return pathname === "/task-board" || pathname === "/task-board/";
}

/**
 * The task board at `/task-board` (`.claude/plans/task-board.md` Part B): read-only, the MCP tools write. `App` mounts it
 * for a SuperAdmin only; the API decides the same on every route. Not linked from the nav, the URL is the entry.
 */
export function TaskBoard({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const tasks = useTaskBoardTasks(session);
  useTaskBoardFocusRefresh(session);
  const [openId, setOpenId] = useState<number | null>(null);

  if (tasks.isPending) {
    return (
      <p role="status" className="text-muted-foreground p-6 text-sm">
        {t("taskBoard.loading")}
      </p>
    );
  }
  // Only with nothing to show: a failed refresh keeps the loaded rows and an open task.
  if (tasks.data === undefined) {
    return (
      <div className="flex flex-col items-start gap-3 p-6">
        <h1 className="sr-only">{t("taskBoard.title")}</h1>
        <p role="alert" className="text-destructive text-sm">
          {t("taskBoard.loadFailed")}
        </p>
        <Button variant="outline" size="sm" onPress={() => void tasks.refetch()} isDisabled={tasks.isFetching}>
          {t("taskBoard.tryAgain")}
        </Button>
      </div>
    );
  }

  const work = tasks.data.filter((x) => x.section !== "konzepte");
  const started = work.filter((x) => x.state === "started").length;

  return (
    <div className="page-column flex flex-col gap-8 py-8">
      <header className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold tracking-tight">{t("taskBoard.title")}</h1>
          <p className="text-muted-foreground m-0 text-sm">{t("taskBoard.intro")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusChip tone="neutral" className="px-3 py-1 text-sm">{t("taskBoard.countOpen", { count: work.length - started })}</StatusChip>
          <StatusChip tone="attention" className="px-3 py-1 text-sm">{t("taskBoard.countStarted", { count: started })}</StatusChip>
          <StatusChip tone="primary" className="px-3 py-1 text-sm">{t("taskBoard.countConcepts", { count: tasks.data.length - work.length })}</StatusChip>
        </div>
      </header>

      <div className="flex flex-col gap-5">
        {TaskBoardSectionSchema.options.map((section) => (
          <SectionList key={section} section={section} tasks={tasks.data.filter((x) => x.section === section)} onOpen={setOpenId} />
        ))}
        <ClosedList session={session} />
      </div>

      {openId !== null ? <TaskDialog session={session} id={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}

const COLLAPSE_KEY = "arc.task-board.expanded";

function readExpanded(): Partial<Record<TaskBoardSection, boolean>> {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? "{}");
    return stored !== null && typeof stored === "object" ? stored : {};
  } catch {
    return {};
  }
}

/** Open or closed per section, remembered in this browser. Until someone chooses: open with tasks, concepts closed. */
function useSectionExpanded(section: TaskBoardSection, hasTasks: boolean): [boolean, (expanded: boolean) => void] {
  const [chosen, setChosen] = useState(() => readExpanded()[section]);
  const set = (expanded: boolean) => {
    setChosen(expanded);
    try {
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify({ ...readExpanded(), [section]: expanded }));
    } catch {
      // Blocked storage only costs the memory, the section still opens and closes.
    }
  };
  return [chosen ?? (hasTasks && section !== "konzepte"), set];
}

function SectionTitle({ name, count, extra }: { name: string; count?: number; extra?: React.ReactNode }) {
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1">
      <span className="text-sm font-semibold tracking-wider uppercase">{name}</span>{" "}
      {count !== undefined ? <span className="text-orange text-sm font-semibold tabular-nums">{count}</span> : null}
      {extra}
    </span>
  );
}

function SectionList({ section, tasks, onOpen }: { section: TaskBoardSection; tasks: TaskBoardTask[]; onOpen: (id: number) => void }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useSectionExpanded(section, tasks.length > 0);
  const name = t(`taskBoard.section.${section}`);
  const started = tasks.filter((x) => x.state === "started").length;
  return (
    <section aria-label={`${name} (${tasks.length})`}>
      <Disclosure
        headingLevel={2}
        isExpanded={expanded}
        onExpandedChange={setExpanded}
        className="bg-muted"
        title={
          <SectionTitle
            name={name}
            count={tasks.length}
            extra={started > 0 ? <StatusChip tone="attention">{t("taskBoard.countStarted", { count: started })}</StatusChip> : null}
          />
        }
      >
        {tasks.length === 0 ? (
          <p className="text-muted-foreground m-0 px-4 py-4 text-sm">{t("taskBoard.empty")}</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-3 p-3 sm:p-4">
            {tasks.map((task) => (
              <li key={task.id}>
                <Row task={task} onOpen={onOpen} />
              </li>
            ))}
          </ul>
        )}
      </Disclosure>
    </section>
  );
}

function Row({ task, onOpen }: { task: TaskBoardTask; onOpen: (id: number) => void }) {
  return (
    <Button variant="ghost" className="bg-background hover:bg-background dark:hover:bg-background border-border hover:border-primary h-auto w-full flex-col items-stretch gap-2 rounded-md border px-4 py-3 text-left font-normal whitespace-normal shadow-sm sm:flex-row sm:items-center sm:justify-between sm:gap-6" onPress={() => onOpen(task.id)}>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-orange font-mono text-xs break-all">{task.slug}</span>
        <span className="text-base font-medium break-words">{task.title}</span>
      </span>
      <TaskMeta task={task} />
    </Button>
  );
}

/** The right side of a row: the state chip, and below it who holds the task and when it last changed. */
function TaskMeta({ task }: { task: TaskBoardTask }) {
  const { i18n, t } = useTranslation();
  const line = [task.assignee, t("taskBoard.version", { version: task.version }), formatDate(task.updatedAt, i18n.language)].filter(Boolean).join(" · ");
  return (
    <span className="flex min-w-0 shrink-0 flex-col items-start gap-1 sm:max-w-[40%] sm:items-end sm:text-right">
      <TaskState task={task} />
      <span className="text-muted-foreground text-xs break-words">{line}</span>
    </span>
  );
}

function TaskState({ task }: { task: TaskBoardTask }) {
  const { t } = useTranslation();
  if (task.section === "konzepte") return null;
  return task.state === "started" ? (
    <StatusChip tone="attention" className="max-w-full rounded-lg break-all">
      {t("taskBoard.startedOn", { branch: task.branch })}
    </StatusChip>
  ) : (
    <StatusChip tone="neutral" className="shrink-0">
      {t("taskBoard.open")}
    </StatusChip>
  );
}

function ClosedList({ session }: { session: AuthSession }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const closed = useTaskBoardClosed(session, expanded);
  const rows = closed.data?.closed ?? [];
  return (
    <Disclosure headingLevel={2} className="bg-muted" title={<SectionTitle name={t("taskBoard.closed")} count={closed.data?.total} />} isExpanded={expanded} onExpandedChange={setExpanded}>
      <div className="flex flex-col gap-2 px-4 py-4">
        {closed.isPending ? <p role="status" className="text-muted-foreground m-0 text-sm">{t("taskBoard.loading")}</p> : null}
        {closed.isError ? <p role="alert" className="text-destructive m-0 text-sm">{t("taskBoard.closedFailed")}</p> : null}
        {closed.data && rows.length === 0 ? <p className="text-muted-foreground m-0 text-sm">{t("taskBoard.closedEmpty")}</p> : null}
        {rows.length > 0 ? (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {rows.map((row) => (
              <li key={row.id} className="flex items-baseline justify-between gap-4 text-sm">
                <span className="min-w-0 break-words">{row.name}</span>
                <span className="text-muted-foreground shrink-0 text-xs">{formatDate(row.closedAt, i18n.language)}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {closed.data && closed.data.total > rows.length ? (
          <p className="text-muted-foreground m-0 text-xs">{t("taskBoard.closedMore", { shown: rows.length, total: closed.data.total })}</p>
        ) : null}
      </div>
    </Disclosure>
  );
}

/** A task's text starts with `# <title>`, and the dialog already shows the title above it. */
function withoutLeadingTitle(markdown: string, title: string): string {
  const firstLine = /^\s*#\s+(.*?)\s*(?:\r?\n|$)/.exec(markdown);
  return firstLine?.[1] === title ? markdown.slice(firstLine[0].length) : markdown;
}

function TaskDialog({ session, id, onClose }: { session: AuthSession; id: number; onClose: () => void }) {
  const { t } = useTranslation();
  const query = useTaskBoardTask(session, id);
  const gone = query.error instanceof TaskGoneError;
  // A failed refresh keeps the text on screen; a task that no longer exists must not.
  const task = gone ? { ...query, data: undefined } : query;
  return (
    <Dialog isOpen size="xl" onOpenChange={(open) => !open && onClose()}>
      <DialogTitle className="break-words">{task.data?.title ?? t("taskBoard.title")}</DialogTitle>
      {task.data ? (
        <div className="flex flex-wrap items-center gap-2">
          <TaskState task={task.data} />
          <span className="text-muted-foreground text-xs">
            {[task.data.assignee, t("taskBoard.version", { version: task.data.version })].filter(Boolean).join(" · ")}
          </span>
        </div>
      ) : null}
      <div tabIndex={0} className="max-h-[65vh] overflow-y-auto">
        {task.isPending ? <p role="status" className="text-muted-foreground m-0 text-sm">{t("taskBoard.loading")}</p> : null}
        {task.isError ? <p role="alert" className="text-destructive m-0 text-sm">{t(gone ? "taskBoard.taskGone" : "taskBoard.taskFailed")}</p> : null}
        {task.data ? <PmMarkdown gfm>{withoutLeadingTitle(task.data.contentMd, task.data.title)}</PmMarkdown> : null}
      </div>
      <div className="flex justify-end">
        <Button variant="default" onPress={onClose}>
          {t("taskBoard.close")}
        </Button>
      </div>
    </Dialog>
  );
}
